import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RefreshCw, Pill, Sparkles, AlertCircle, Watch, Settings,
  Sun, Moon as MoonIcon, CloudSun, TrendingUp, TrendingDown, ChevronRight, Target,
  Utensils, Dumbbell, Brain, Activity, Plus, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AdvancedDigitalTwin } from '../components/AdvancedDigitalTwin';
import { VitalCard } from '../components/VitalCard';
import { CoachMessage } from '../components/CoachMessage';
import { MoodSlider } from '../components/MoodSlider';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import FEATURE_FLAGS from '../config/featureFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const greetingIcons = {
  morning: Sun,
  afternoon: CloudSun,
  evening: MoonIcon,
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [coachMessage, setCoachMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [showDataPrompt, setShowDataPrompt] = useState(false);
  const [habitNotification, setHabitNotification] = useState(null);

  const fetchDashboard = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [dashRes, coachRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/coach360/today`)
      ]);
      setDashboard(dashRes.data);
      setCoachMessage(coachRes.data);

      // Check habit notification
      const habit = dashRes.data?.active_habit;
      if (habit && habit.id) {
        const today = new Date().toISOString().split('T')[0];
        const isCompletedToday = habit.completed_days?.includes(today);
        setHabitNotification(isCompletedToday ? null : habit);
      } else {
        setHabitNotification(null);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (user && user.device_synced === false) {
      setShowDataPrompt(true);
    }
  }, [user]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard(false);
  };

  const handleMoodSave = async (score) => {
    try {
      await axios.post(`${API}/mood/checkin`, { score });
      toast.success('Mood saved!');
      setShowMoodModal(false);
      fetchDashboard(false);
    } catch (error) {
      toast.error('Failed to save mood');
    }
  };

  const handleConfirmHabit = async (habitId) => {
    try {
      await axios.post(`${API}/habits/confirm`, { habit_id: habitId });
      toast.success('Great job! Habit marked complete!');
      setHabitNotification(null);
      fetchDashboard(false);
    } catch (error) {
      toast.error('Failed to confirm habit');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={32} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your health snapshot...</p>
        </div>
      </div>
    );
  }

  const { dts, biometrics, mood, weekly_review } = dashboard || {};
  const greeting = getGreeting();
  const GreetingIcon = greetingIcons[greeting];

  const getMoodTrend = () => {
    if (!mood?.history || mood.history.length < 2) return null;
    const recent = mood.history.slice(-3);
    const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
    if (mood.score > avg + 0.5) return { text: 'Better than last 3 days', trend: 'up' };
    if (mood.score < avg - 0.5) return { text: 'Lower than last 3 days', trend: 'down' };
    return { text: 'Same as yesterday', trend: 'steady' };
  };

  const moodTrend = getMoodTrend();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background pb-nav">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <motion.div
                initial={{ rotate: -20, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
              >
                <GreetingIcon size={24} className="text-[#F8B64C]" />
              </motion.div>
              <div>
                <p className="text-xs text-muted-foreground">Good {greeting}</p>
                <h1 className="font-heading font-bold text-lg">
                  Hi, {user?.name?.split(' ')[0] || 'there'}!
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className="rounded-full"
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
                className="rounded-full"
                data-testid="settings-btn"
              >
                <Settings size={18} />
              </Button>
              <ThemeToggle />
              <ProfileAvatar size="sm" />
            </div>
          </div>
        </header>

        <main className="px-4 py-6 space-y-6">
          {/* Missing Data Prompt - only show device sync, no facial scan for MVP */}
          <AnimatePresence>
            {showDataPrompt && (
              <motion.section
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Card className="glass-card p-4 border-l-4 border-l-[#F8B64C]">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-[#F8B64C] shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Connect your devices</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Sync your wearable for more accurate Digital Twin insights.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => navigate('/onboarding/device')}
                          className="bg-primary hover:bg-primary/90 rounded-full text-xs"
                        >
                          <Watch size={14} className="mr-1" />
                          Sync Device
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowDataPrompt(false)}
                          className="text-xs text-muted-foreground"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.section>
            )}
          </AnimatePresence>

          {/* Advanced Digital Twin */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="digital-twin-section"
          >
            <AdvancedDigitalTwin
              score={dts?.score || 0}
              trajectory={dts?.trajectory || 'steady'}
              confidence={dts?.confidence || 'High'}
              topDrivers={dts?.top_drivers || []}
              breakdown={{
                biometrics: 75,
                behavior: 70,
                sleep: biometrics?.sleep_hours ? Math.min(100, (biometrics.sleep_hours / 8) * 100) : 65,
                mood: dashboard?.mood?.score ? dashboard.mood.score * 10 : 70,
                activity: biometrics?.steps ? Math.min(100, (biometrics.steps / 10000) * 100) : 60,
              }}
              biometrics={{
                hrv: biometrics?.hrv,
                sleep_hours: biometrics?.sleep_hours,
                steps: biometrics?.steps,
              }}
              visceralFat={biometrics?.visceral_fat ?? 14}
              trendData={[]}
              gender={user?.gender || 'unknown'}
              onViewTrends={() => navigate('/trends')}
              onRescan={FEATURE_FLAGS.FACIAL_SCANNING_ENABLED ? () => navigate('/scan/start') : null}
              onFixData={() => navigate('/onboarding/device')}
              onConnectCare={() => navigate('/care')}
              onViewDrivers={() => {
                toast.info('Top drivers affect your Digital Twin Score', {
                  description: dts?.top_drivers?.join(', ') || 'No specific drivers today'
                });
              }}
            />
          </motion.section>

          {/* Primary Actions - Check In, Today's Plan, Essentials */}
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Quick Actions
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors active:scale-[0.98]"
                onClick={() => setShowMoodModal(true)}
                data-testid="quick-checkin"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="p-2 rounded-xl bg-primary/20">
                    <Heart size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Check In</p>
                    <p className="text-xs text-muted-foreground">Mood & Status</p>
                  </div>
                </div>
              </Card>
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors active:scale-[0.98]"
                onClick={() => navigate('/daily-plan')}
                data-testid="quick-todays-plan"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="p-2 rounded-xl bg-[#F8B64C]/20">
                    <Sparkles size={18} className="text-[#F8B64C]" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Today&apos;s Plan</p>
                    <p className="text-xs text-muted-foreground">Meals & Workout</p>
                  </div>
                </div>
              </Card>
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors active:scale-[0.98]"
                onClick={() => navigate('/essentials')}
                data-testid="quick-essentials"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="p-2 rounded-xl bg-[#8BB89F]/20">
                    <Pill size={18} className="text-[#8BB89F]" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Essentials</p>
                    <p className="text-xs text-muted-foreground">Meds & Supps</p>
                  </div>
                </div>
              </Card>
            </div>
          </section>

          {/* Secondary Actions - Nutrition, Exercise, Mood, Habits */}
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Track & Log
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/daily-plan')}
                data-testid="track-nutrition"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[#F8B64C]/10">
                    <Utensils size={18} className="text-[#F8B64C]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Nutrition</p>
                    <p className="text-xs text-muted-foreground">Log meals</p>
                  </div>
                  <Plus size={16} className="text-muted-foreground" />
                </div>
              </Card>
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/daily-plan')}
                data-testid="track-exercise"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[#D54B48]/10">
                    <Dumbbell size={18} className="text-[#D54B48]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Exercise</p>
                    <p className="text-xs text-muted-foreground">Log workout</p>
                  </div>
                  <Plus size={16} className="text-muted-foreground" />
                </div>
              </Card>
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/symptoms')}
                data-testid="track-symptoms"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[#C10891]/10">
                    <Activity size={18} className="text-[#C10891]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Symptoms</p>
                    <p className="text-xs text-muted-foreground">How you feel</p>
                  </div>
                  <Plus size={16} className="text-muted-foreground" />
                </div>
              </Card>
              <Card 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/habits')}
                data-testid="track-habits"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10">
                    <Target size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Habits</p>
                    <p className="text-xs text-muted-foreground">Daily routines</p>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </div>
              </Card>
            </div>
          </section>

          {/* Mood Assessment */}
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Mood Assessment
            </h2>
            <Card 
              className="glass-card p-5 cursor-pointer hover:border-primary/30 transition-colors active:scale-[0.99]"
              onClick={() => setShowMoodModal(true)}
              data-testid="mood-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">How are you feeling today?</p>
                  {mood?.score ? (
                    <>
                      <p className="text-2xl font-bold font-heading mt-1" style={{ color: getMoodColor(mood.score) }}>
                        {mood.score}/10
                      </p>
                      {moodTrend && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          {moodTrend.trend === 'up' && <TrendingUp size={12} className="text-[#8BB89F]" />}
                          {moodTrend.trend === 'down' && <TrendingDown size={12} className="text-[#D54B48]" />}
                          {moodTrend.text}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-primary font-medium mt-1">Tap to check in</p>
                  )}
                </div>
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center relative"
                  style={{ 
                    backgroundColor: mood?.score ? `${getMoodColor(mood.score)}20` : 'hsl(var(--muted))',
                    border: mood?.score ? `2px solid ${getMoodColor(mood.score)}` : 'none'
                  }}
                >
                  {mood?.score && (
                    <div 
                      className="absolute inset-0 rounded-full opacity-30"
                      style={{
                        background: `linear-gradient(to top, ${getMoodColor(mood.score)} ${mood.score * 10}%, transparent ${mood.score * 10}%)`
                      }}
                    />
                  )}
                  <span className="text-2xl font-bold relative z-10" style={{ color: mood?.score ? getMoodColor(mood.score) : 'hsl(var(--muted-foreground))' }}>
                    {mood?.score || '?'}
                  </span>
                </div>
              </div>
            </Card>
          </section>

          {/* Coach360 */}
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Coach360
            </h2>
            <CoachMessage
              message={coachMessage?.message}
              suggestedAction={coachMessage?.suggested_action}
              onClick={() => navigate('/coach')}
            />
          </section>

          {/* Habit Notification — only shows when habit not marked today */}
          <AnimatePresence>
            {habitNotification && (
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Card className="glass-card p-4 border-l-4 border-l-primary" data-testid="habit-notification">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-primary/10">
                        <Target size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{habitNotification.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Day {habitNotification.current_streak + 1} of 21 — not marked today
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleConfirmHabit(habitNotification.id)}
                        className="rounded-full text-xs bg-primary hover:bg-primary/90"
                        data-testid="habit-notify-complete-btn"
                      >
                        Done
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate('/habits')}
                        className="rounded-full text-xs"
                      >
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.section>
            )}
          </AnimatePresence>
        </main>

        {/* Mood Modal */}
        <AnimatePresence>
          {showMoodModal && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-sm"
              >
                <Card className="glass-card p-6">
                  <h2 className="font-heading font-bold text-xl text-center mb-6">Mood Check-In</h2>
                  <MoodSlider initialValue={mood?.score || 5} onSave={handleMoodSave} />
                  <Button
                    variant="ghost"
                    className="w-full mt-4"
                    onClick={() => setShowMoodModal(false)}
                  >
                    Cancel
                  </Button>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <BottomNav />
      </div>
    </TooltipProvider>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function getMoodColor(score) {
  if (score <= 3) return '#D54B48';
  if (score <= 5) return '#F8B64C';
  if (score <= 7) return '#8BB89F';
  return '#C10891';
}
